import { useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import type { RandomizerNodeData } from '../../../../nodes/randomizer-node/randomizer.node'
import type { BuilderNodeType } from '../../../../types'

type RandomizerNodePropertyPanelProps = Readonly<{
  id: string
  type: BuilderNodeType
  data: RandomizerNodeData
  updateData: (data: Partial<RandomizerNodeData>) => void
}>

export default function RandomizerNodePropertyPanel({
  id,
  data,
  updateData,
}: RandomizerNodePropertyPanelProps) {
  const paths = useMemo(() => data.paths, [data.paths])

  function updatePath(
    index: number,
    partial: Partial<RandomizerNodeData['paths'][number]>
  ) {
    const next = paths.map((p, i) => (i === index ? { ...p, ...partial } : p))
    updateData({ paths: next })
  }

  const total = paths.reduce((acc, p) => acc + (Number(p.percentage) || 0), 0)
  const isTotalValid = total === 100

  return (
    <div className='flex flex-col gap-4 p-4'>
      <div className='self-end -mb-4 text-xs text-card-foreground/20'>{id}</div>

      <div className='flex flex-col gap-2'>
        <div className='text-xs font-semibold text-card-foreground/60'>
          Distribuição (%)
        </div>

        {paths.map((p, index) => (
          <div key={p.id} className='grid items-center grid-cols-3 gap-2'>
            <div className='col-span-2'>
              <Label className='block mb-1 text-xs'>Rótulo</Label>
              <Input
                value={p.label}
                onChange={(e) => updatePath(index, { label: e.target.value })}
                placeholder={`Caminho ${index + 1}`}
              />
            </div>
            <div className='col-span-1'>
              <Label className='block mb-1 text-xs'>%</Label>
              <div className='flex items-center gap-2'>
                <Slider
                  value={[p.percentage]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={([val]) =>
                    updatePath(index, { percentage: val })
                  }
                />
                <div className='w-10 text-xs text-right'>{p.percentage}%</div>
              </div>
            </div>
          </div>
        ))}

        <div
          className={`text-xs ${isTotalValid ? 'text-green-600' : 'text-red-600'}`}
        >
          Total: {total}% {isTotalValid ? '(ok)' : '(deve somar 100%)'}
        </div>
      </div>

      <div className='text-xs text-card-foreground/60'>
        Ex.: 50% - A, 50% - B
      </div>
    </div>
  )
}
